import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { communityPosts, hotTopics } from '@/mocks/community';

export default function CommunityPage() {
  const [topic, setTopic] = useState('全部');
  const [likedIds, setLikedIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [toast, setToast] = useState('');

  const posts = useMemo(
    () => (topic === '全部' ? communityPosts : communityPosts.filter((p) => p.topic === topic)),
    [topic],
  );

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const toggleLike = (id: string) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-700">视频社交</span>
      </nav>

      <header className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">视频社交互动</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
            与同样热爱影视与视频的朋友聊聊观后感、分享片单、参与话题讨论。
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-edit-2-line" />
          参与发帖
        </button>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-4">
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-200/70 p-1">
            {hotTopics.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTopic(t)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  topic === t
                    ? 'bg-background-50 text-primary-600 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {posts.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-chat-off-line text-3xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-500">该话题下暂时还没有动态</p>
              <p className="mt-1 text-xs text-foreground-400">社区活跃度较低，快来发布第一条内容吧</p>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {posts.map((post) => {
                const liked = likedIds.includes(post.id);
                return (
                  <article
                    key={post.id}
                    className="rounded-lg border border-background-200/70 bg-background-50 p-5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img
                          src={post.avatar}
                          alt={post.author}
                          title={`${post.author} 的头像`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900">{post.author}</p>
                        <p className="text-xs text-foreground-400">{post.time}</p>
                      </div>
                      <span className="ml-auto rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                        #{post.topic}
                      </span>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-foreground-700">{post.content}</p>

                    <div className="mt-4 flex items-center gap-5 border-t border-background-200/70 pt-4 text-sm text-foreground-400">
                      <button
                        type="button"
                        onClick={() => toggleLike(post.id)}
                        className={`flex items-center gap-1.5 transition-colors ${
                          liked ? 'text-primary-600' : 'hover:text-primary-600'
                        }`}
                      >
                        <i className={liked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                        {post.likes + (liked ? 1 : 0)}
                      </button>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-chat-3-line" />
                        {post.comments}
                      </span>
                      <button
                        type="button"
                        onClick={() => showToast('分享链接已复制（演示）')}
                        className="ml-auto flex items-center gap-1.5 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-share-forward-line" />
                        分享
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <i className="ri-fire-line text-primary-500" />
              热门话题导航
            </h2>
            <div className="mt-4 flex flex-col gap-2">
              {hotTopics
                .filter((t) => t !== '全部')
                .map((t, i) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTopic(t)}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                      topic === t
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${
                        i < 2 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate">{t}</span>
                    <i className="ri-arrow-right-s-line text-foreground-300" />
                  </button>
                ))}
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-add-line" />
              发布新动态
            </button>
          </div>
        </aside>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-background-950/50"
            onClick={() => setModalOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-lg rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-900">发布动态（演示）</h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label="关闭"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="mt-2 text-xs text-foreground-400">
              本页面为前端演示，暂不支持真实发布，内容不会真正提交。
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 500))}
              rows={5}
              maxLength={500}
              placeholder="分享你对某部影视或视频的看法…"
              className="mt-4 w-full resize-none rounded-md border border-background-200 bg-background-100 p-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
            />
            <div className="mt-1 text-right text-xs text-foreground-400">{draft.length}/500</div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                  setDraft('');
                  showToast('演示模式：暂不支持真实发布');
                }}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                发布
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-[80] -translate-x-1/2 rounded-md bg-foreground-900 px-4 py-2.5 text-sm text-background-50 shadow-lg md:bottom-10">
          {toast}
        </div>
      )}
    </div>
  );
}